const SVG_NS = 'http://www.w3.org/2000/svg'
const FILTER_ROOT_ID = 'laosan-liquid-glass-filters'
const HAS_DOM = typeof window !== 'undefined' && typeof document !== 'undefined'
const HTMLElementBase = typeof HTMLElement === 'undefined' ? class {} : HTMLElement

const mapCache = new Map()
const MAP_CACHE_LIMIT = 48
// 滤镜安全区（padding）上下限：保证边缘折射能采样到元素边界外的背景，
// 又避免超大面板的贴图与滤镜区域无限放大。
const MIN_FILTER_PADDING_PX = 16
const MAX_FILTER_PADDING_PX = 64
const connectedGlasses = new Set()

export const LIQUID_GLASS_PERFORMANCE_DEFAULTS = Object.freeze({
  fpsThreshold: 40,
  sampleMs: 1000,
  badWindowsNeeded: 3,
  graceMs: 4000
})

const performanceOptions = { ...LIQUID_GLASS_PERFORMANCE_DEFAULTS }
let performanceDegraded = false
let performanceMonitorStarted = false
let performanceFrame = 0
let lastMeasuredFps = null

const clamp = (value, min, max) => Math.min(Math.max(value, min), max)

const smoothStep = (min, max, value) => {
  const t = clamp((value - min) / (max - min), 0, 1)
  return t * t * (3 - 2 * t)
}

const toNumber = (value, fallback) => {
  const parsed = Number.parseFloat(value)
  return Number.isFinite(parsed) ? parsed : fallback
}

// 把 CSS 颜色(#rgb/#rrggbb/#rrggbbaa/rgb()/rgba())解析为 [r,g,b]。
// 供 surface 染色背景使用:用 rgba() 而非 color-mix(),因为 html2canvas
// 无法解析 color-mix,会在自适应采样栅格化整页时抛错(且兼容旧浏览器)。
const parseTintToRgb = (value) => {
  if (typeof value !== 'string') return null
  const v = value.trim().toLowerCase()
  let m = /^#([0-9a-f]{3,8})$/.exec(v)
  if (m) {
    const h = m[1]
    const hex = h.length <= 4 ? h.split('').map((c) => c + c).join('') : h
    return [0, 2, 4].map((i) => parseInt(hex.slice(i, i + 2), 16))
  }
  m = /^rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)/.exec(v)
  if (m) return [Number(m[1]), Number(m[2]), Number(m[3])]
  return null
}

const tintToRgba = (tint, alpha) => {
  const rgb = parseTintToRgb(tint) || [255, 255, 255]
  return `rgba(${rgb[0]},${rgb[1]},${rgb[2]},${clamp(alpha, 0, 1)})`
}

const toRatio = (value, fallback) => {
  if (typeof value === 'string' && value.trim().endsWith('%')) {
    return toNumber(value, fallback * 100) / 100
  }
  return toNumber(value, fallback)
}

export const LIQUID_GLASS_DEFAULTS = Object.freeze({
  refraction: 0.55,
  blur: 3,
  brightness: 1.03,
  saturation: 1.45,
  tint: '#ffffff',
  tintOpacity: 0.18,
  radius: 24,
  bezel: 0.5,
  specular: 0.22,
  interactive: true,
  draggable: false,
  morph: false,
  fixed: false,
  adaptive: false,
  adaptiveMaxBrightness: 0.18,
  adaptiveMaxBlur: 6,
  adaptiveSpeed: 0.2
})

export function normalizeLiquidGlassOptions(input = {}) {
  return {
    refraction: clamp(toNumber(input.refraction, LIQUID_GLASS_DEFAULTS.refraction), 0, 1.5),
    blur: clamp(toNumber(input.blur, LIQUID_GLASS_DEFAULTS.blur), 0, 40),
    brightness: clamp(toRatio(input.brightness, LIQUID_GLASS_DEFAULTS.brightness), 0.5, 2),
    saturation: clamp(toRatio(input.saturation, LIQUID_GLASS_DEFAULTS.saturation), 0, 3),
    tint: typeof input.tint === 'string' && input.tint.trim()
      ? input.tint.trim()
      : LIQUID_GLASS_DEFAULTS.tint,
    tintOpacity: clamp(toNumber(input.tintOpacity, LIQUID_GLASS_DEFAULTS.tintOpacity), 0, 1),
    radius: clamp(toNumber(input.radius, LIQUID_GLASS_DEFAULTS.radius), 0, 999),
    bezel: clamp(toNumber(input.bezel, LIQUID_GLASS_DEFAULTS.bezel), 0.05, 1),
    specular: clamp(toNumber(input.specular, LIQUID_GLASS_DEFAULTS.specular), 0, 1),
    interactive: input.interactive === undefined
      ? LIQUID_GLASS_DEFAULTS.interactive
      : !(input.interactive === false || input.interactive === 'false'),
    draggable: input.draggable === undefined
      ? LIQUID_GLASS_DEFAULTS.draggable
      : !(input.draggable === false || input.draggable === 'false'),
    morph: input.morph === undefined
      ? LIQUID_GLASS_DEFAULTS.morph
      : !(input.morph === false || input.morph === 'false'),
    fixed: input.fixed === undefined
      ? LIQUID_GLASS_DEFAULTS.fixed
      : !(input.fixed === false || input.fixed === 'false'),
    adaptive: input.adaptive === undefined
      ? LIQUID_GLASS_DEFAULTS.adaptive
      : !(input.adaptive === false || input.adaptive === 'false'),
    adaptiveMaxBrightness: clamp(toNumber(input.adaptiveMaxBrightness, LIQUID_GLASS_DEFAULTS.adaptiveMaxBrightness), 0, 0.5),
    adaptiveMaxBlur: clamp(toNumber(input.adaptiveMaxBlur, LIQUID_GLASS_DEFAULTS.adaptiveMaxBlur), 0, 20),
    adaptiveSpeed: clamp(toNumber(input.adaptiveSpeed, LIQUID_GLASS_DEFAULTS.adaptiveSpeed), 0.02, 1)
  }
}

export function configureLiquidGlassPerformance(input = {}) {
  if (input.fpsThreshold !== undefined) {
    performanceOptions.fpsThreshold = clamp(toNumber(input.fpsThreshold, LIQUID_GLASS_PERFORMANCE_DEFAULTS.fpsThreshold), 1, 240)
  }
  if (input.sampleMs !== undefined) {
    performanceOptions.sampleMs = clamp(toNumber(input.sampleMs, 1000), 100, 10000)
  }
  if (input.badWindowsNeeded !== undefined) {
    performanceOptions.badWindowsNeeded = Math.round(clamp(
      toNumber(input.badWindowsNeeded, 3),
      1,
      10
    ))
  }
  if (input.graceMs !== undefined) {
    performanceOptions.graceMs = clamp(toNumber(input.graceMs, 4000), 0, 30000)
  }
  return getLiquidGlassPerformanceState()
}

export function getLiquidGlassPerformanceState() {
  return {
    ...performanceOptions,
    degraded: performanceDegraded,
    lastFps: lastMeasuredFps,
    connectedCount: connectedGlasses.size
  }
}

/* ---------------- 背景自适应采样引擎 ----------------
   浏览器禁止直接读取 backdrop-filter 背后的已合成像素，因此借助 html2canvas
   对玻璃所在区域截图（截图瞬间把玻璃自身隐藏，避免把自己截进去），再降到
   低分辨率读像素，估算平均亮度与亮度方差。html2canvas 体积较大，故仅在
   有组件开启 adaptive 时才动态加载，未安装/加载失败时静默回退，不影响其余能力。 */
let html2canvasLoader = null
function loadHtml2Canvas() {
  if (!HAS_DOM) return Promise.resolve(null)
  if (typeof window !== 'undefined' && window.html2canvas) {
    return Promise.resolve(window.html2canvas)
  }
  if (!html2canvasLoader) {
    // 通过间接 specifier 动态加载：打包器(webpack/rollup/vite)能解析并分包；
    // 无打包器的 global 版请先引入 html2canvas 的 CDN(挂到 window.html2canvas)。
    const specifier = 'html2canvas'
    html2canvasLoader = import(/* @vite-ignore */ specifier)
      .then((mod) => (mod && (mod.default || mod)) || null)
      .catch(() => null)
  }
  return html2canvasLoader
}

const ADAPTIVE_SAMPLE_SIZE = 32 // 采样分辨率：足够估算亮度/方差，避免高开销
const ADAPTIVE_SAMPLE_COOLDOWN_MS = 800 // 两次整页栅格化的最小间隔,避免交互卡顿

function analyzeBackdropPixels(sourceCanvas) {
  const size = ADAPTIVE_SAMPLE_SIZE
  const canvas = document.createElement('canvas')
  canvas.width = size
  canvas.height = size
  const ctx = canvas.getContext('2d', { willReadFrequently: true })
  if (!ctx) return null
  ctx.drawImage(sourceCanvas, 0, 0, size, size)
  let data
  try {
    data = ctx.getImageData(0, 0, size, size).data
  } catch {
    return null
  }
  let sum = 0
  let sumSq = 0
  let count = 0
  for (let i = 0; i < data.length; i += 4) {
    const alpha = data[i + 3]
    // 跳过透明/半透明像素:html2canvas 用 backgroundColor:null 截图,被 ignoreElements
    // 跳过的玻璃区域是透明像素 rgba(0,0,0,0)。若把它们的 RGB 当纯黑计入亮度,
    // luminance 被持续拉低 → 玻璃越调越暗,形成正反馈("放久了慢慢变黑")。
    // 只统计真实不透明的背景像素,并按 alpha 加权。
    if (alpha < 16) continue
    const weight = alpha / 255
    // Rec.601 相对亮度，0(暗) → 1(亮)
    const lum = (0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2]) / 255
    sum += lum * weight
    sumSq += lum * lum * weight
    count += weight
  }
  if (count < 1) return null // 几乎没有有效背景像素,放弃本次采样
  const mean = sum / count
  const variance = Math.max(0, sumSq / count - mean * mean)
  return { luminance: mean, stddev: Math.sqrt(variance) }
}

function supportsSvgRefraction() {
  return HAS_DOM && (
    CSS.supports('backdrop-filter', 'url(#x)') ||
    CSS.supports('-webkit-backdrop-filter', 'url(#x)')
  )
}

function degradeAllLiquidGlass(fps) {
  if (performanceDegraded) return
  performanceDegraded = true
  lastMeasuredFps = fps
  cancelAnimationFrame(performanceFrame)
  performanceFrame = 0
  connectedGlasses.forEach((element) => element._applyGaussianFallback('low-fps', fps))
  window.dispatchEvent(new CustomEvent('liquid-glass-degraded', {
    detail: {
      reason: 'low-fps',
      fps,
      threshold: performanceOptions.fpsThreshold,
      fallback: 'gaussian'
    }
  }))
}

function startPerformanceMonitor() {
  if (
    performanceMonitorStarted ||
    performanceDegraded ||
    !supportsSvgRefraction()
  ) return

  performanceMonitorStarted = true
  // 用「上一帧时间戳」累计真实流逝时间,而非数帧数后除以 elapsed。
  // 旧实现 elapsed>=sampleMs 时直接 frames=0/windowStart=now,溢出那一帧的时间被吞,
  // fps 系统性偏低;且暂停窗口(elapsed>sampleMs*2)只是不计入 fps 却没清 badWindows,
  // 暂停前攒的坏窗口会带回来后继续累加 → 实际没持续低于阈值也被误降级(用户反馈)。
  let last = 0 // 上一帧时间戳,0 表示尚未起步
  let accumulated = 0 // 当前窗口已累积的有效帧时长(ms)
  let windowFrames = 0 // 当前窗口内的有效帧数
  let badWindows = 0
  const graceUntil = performance.now() + performanceOptions.graceMs

  document.addEventListener('visibilitychange', () => {
    // 切回前台:整窗重起,丢弃暂停前后的所有统计,避免脏数据带回来后误判
    if (!document.hidden) {
      last = 0
      accumulated = 0
      windowFrames = 0
      badWindows = 0
    }
  })

  const tick = (now) => {
    if (performanceDegraded) return
    if (!connectedGlasses.size) {
      last = 0
      accumulated = 0
      windowFrames = 0
      performanceFrame = requestAnimationFrame(tick)
      return
    }

    const sampleMs = performanceOptions.sampleMs
    if (last > 0) {
      const delta = now - last
      // 单帧间隔超过 sampleMs 说明主线程被暂停/页面刚切回,这一整段不计入统计
      if (delta <= sampleMs) {
        accumulated += delta
        windowFrames += 1
      }
      if (accumulated >= sampleMs && windowFrames > 0) {
        if (now >= graceUntil) {
          // 平均帧率 = 帧数 / 这些帧真实占用的时长
          const fps = windowFrames * 1000 / accumulated
          lastMeasuredFps = fps
          badWindows = fps < performanceOptions.fpsThreshold ? badWindows + 1 : 0
          if (badWindows >= performanceOptions.badWindowsNeeded) {
            degradeAllLiquidGlass(fps)
            return
          }
        }
        accumulated = 0
        windowFrames = 0
      }
    }
    last = now
    performanceFrame = requestAnimationFrame(tick)
  }

  performanceFrame = requestAnimationFrame(tick)
}

function nextFilterId() {
  const key = '__LAOSAN_LIQUID_GLASS_UID__'
  const current = Number(globalThis[key]) || 0
  globalThis[key] = current + 1
  return `laosan-liquid-glass-${current}`
}

/* ================================================================
   液态拖拽形变（移植自桌面 Blog 项目的最佳实践）：
   按压自适应放大 → 拖拽时以速度平滑跟随指针（惯性 + 位移钳制）→
   二维液态拉伸（主轴拉长、另一轴体积守恒收窄）→ 高速叠加动态模糊 →
   松手走欠阻尼弹簧回弹（QQ 弹弹）。
   位移用独立的 translate/scale CSS 属性而非 transform，
   因此与元素自身定位、入场动画以及折射/高斯降级完全解耦。
   ================================================================ */
const DEFORM_MOVE_RATIO = 0.08 // 指针位移 → 流体速度
const DEFORM_TOUCH_MOVE_RATIO = 0.22 // 触屏行程短，需要更直接的位移反馈
const DEFORM_FLUID_SMOOTH = 0.75 // 速度平滑系数（越大越黏）
const DEFORM_RESET_LERP = 0.15 // 松手后位置回弹速度
const DEFORM_CLICK_SUPPRESS_PX = 6 // 拖拽超过该距离时抑制误触点击
const DEFORM_CLICK_SUPPRESS_MS = 700 // 给移动端延迟 click 留出抑制窗口
const DEFORM_LONG_PRESS_MS = 350 // 触屏长按该时长后进入拖拽模式
const DEFORM_SLOP_PX = 12 // 长按计时内滑过该距离 = 滚动意图，放弃形变
const DEFORM_AXIS_STRETCH_MAX = 0.14 // 单轴最大拉伸比例
const DEFORM_CROSS_SQUASH_RATIO = 0.36 // 主轴拉伸时另一轴同步收缩
const DEFORM_STRETCH_LERP = 0.24 // 拖拽中长宽变化的跟随速度
const DEFORM_MOTION_BLUR_MAX = 3 // 高速拖拽叠加的动态模糊上限（px）

const deformPointer = { x: 0, y: 0 } // 全局指针位置（拖拽 deltas 的来源）
const activeDeforms = new Set() // 正在形变或回弹中的 element
let deformFrame = 0
let deformListenersBound = false

function bindDeformGlobalListeners() {
  if (deformListenersBound || !HAS_DOM) return
  deformListenersBound = true
  window.addEventListener('pointermove', (event) => {
    deformPointer.x = event.clientX
    deformPointer.y = event.clientY
  }, { passive: true })
  const release = (event) => {
    for (const element of Array.from(activeDeforms)) {
      if (element._deform?.pointerId === event.pointerId) element._releaseDeform(event)
    }
  }
  window.addEventListener('pointerup', release, { passive: true })
  window.addEventListener('pointercancel', release, { passive: true })
}

function deformLoop() {
  for (const element of Array.from(activeDeforms)) element._stepDeform()
  deformFrame = activeDeforms.size ? requestAnimationFrame(deformLoop) : 0
}

function ensureFilterRoot() {
  let svg = document.getElementById(FILTER_ROOT_ID)
  if (svg) return svg.querySelector('defs') || svg

  svg = document.createElementNS(SVG_NS, 'svg')
  svg.id = FILTER_ROOT_ID
  svg.setAttribute('aria-hidden', 'true')
  svg.setAttribute('width', '0')
  svg.setAttribute('height', '0')
  svg.style.cssText = 'position:fixed;width:0;height:0;overflow:hidden;pointer-events:none'
  const defs = document.createElementNS(SVG_NS, 'defs')
  svg.appendChild(defs)
  ;(document.body || document.documentElement).appendChild(svg)
  return defs
}

function buildDisplacementMap(width, height, radius, bezel, refraction, padding) {
  const key = [width, height, radius, bezel, refraction, padding]
    .map((value) => Number(value).toFixed(3))
    .join('|')
  const cached = mapCache.get(key)
  if (cached) return cached

  const mapWidth = width + padding * 2
  const mapHeight = height + padding * 2
  const longSide = Math.max(mapWidth, mapHeight)
  const downsample = longSide > 260 ? Math.min(3, longSide / 180) : 1
  const sampleWidth = Math.max(1, Math.round(mapWidth / downsample))
  const sampleHeight = Math.max(1, Math.round(mapHeight / downsample))
  const canvas = document.createElement('canvas')
  canvas.width = sampleWidth
  canvas.height = sampleHeight
  const context = canvas.getContext('2d')
  const image = context.createImageData(sampleWidth, sampleHeight)
  const vectors = new Float32Array(sampleWidth * sampleHeight * 2)
  const halfWidth = width / 2
  const halfHeight = height / 2
  let maxDisplacement = 0.0001

  for (let y = 0; y < sampleHeight; y += 1) {
    for (let x = 0; x < sampleWidth; x += 1) {
      const px = x * downsample + 0.5 - padding - halfWidth
      const py = y * downsample + 0.5 - padding - halfHeight
      const qx = Math.abs(px) - halfWidth + radius
      const qy = Math.abs(py) - halfHeight + radius
      const distance = Math.min(Math.max(qx, qy), 0) +
        Math.hypot(Math.max(qx, 0), Math.max(qy, 0)) - radius
      // 中心几乎无形变，越靠边缘位移越大（与 Blog 一致：仅按 SDF 内部衰减）。
      let mask = distance <= 0
        ? smoothStep(-bezel, 0, distance)
        : padding > 0
          ? 1 - smoothStep(0, padding, distance)
          : 0
      mask *= mask

      let normalX
      let normalY
      if (qx > 0 && qy > 0) {
        const cornerLength = Math.hypot(qx, qy) || 1
        normalX = Math.sign(px || 1) * qx / cornerLength
        normalY = Math.sign(py || 1) * qy / cornerLength
      } else if (qx > qy) {
        normalX = Math.sign(px || 1)
        normalY = 0
      } else {
        normalX = 0
        normalY = Math.sign(py || 1)
      }

      const amplitude = mask * refraction * bezel
      const dx = -normalX * amplitude
      const dy = -normalY * amplitude
      const index = (y * sampleWidth + x) * 2
      vectors[index] = dx
      vectors[index + 1] = dy
      maxDisplacement = Math.max(maxDisplacement, Math.abs(dx), Math.abs(dy))
    }
  }

  const displacementScale = maxDisplacement * 2
  for (let vectorIndex = 0, pixelIndex = 0; vectorIndex < vectors.length; vectorIndex += 2, pixelIndex += 4) {
    image.data[pixelIndex] = clamp(vectors[vectorIndex] / displacementScale + 0.5, 0, 1) * 255
    image.data[pixelIndex + 1] = clamp(vectors[vectorIndex + 1] / displacementScale + 0.5, 0, 1) * 255
    image.data[pixelIndex + 2] = 128
    image.data[pixelIndex + 3] = 255
  }
  context.putImageData(image, 0, 0)

  const result = {
    url: canvas.toDataURL(),
    scale: displacementScale,
    mapWidth,
    mapHeight
  }
  if (mapCache.size >= MAP_CACHE_LIMIT) mapCache.delete(mapCache.keys().next().value)
  mapCache.set(key, result)
  return result
}

function readElementOptions(element) {
  return normalizeLiquidGlassOptions({
    refraction: element.getAttribute('refraction'),
    blur: element.getAttribute('blur'),
    brightness: element.getAttribute('brightness'),
    saturation: element.getAttribute('saturation'),
    tint: element.getAttribute('tint'),
    tintOpacity: element.getAttribute('tint-opacity'),
    radius: element.getAttribute('radius'),
    bezel: element.getAttribute('bezel'),
    specular: element.getAttribute('specular'),
    interactive: element.getAttribute('interactive') ?? undefined,
    draggable: element.getAttribute('draggable') ?? undefined,
    morph: element.getAttribute('morph') ?? undefined,
    fixed: element.getAttribute('fixed') ?? undefined,
    adaptive: element.getAttribute('adaptive') ?? undefined,
    adaptiveMaxBrightness: element.getAttribute('adaptive-max-brightness'),
    adaptiveMaxBlur: element.getAttribute('adaptive-max-blur'),
    adaptiveSpeed: element.getAttribute('adaptive-speed')
  })
}

export class LiquidGlassElement extends HTMLElementBase {
  static get observedAttributes() {
    return [
      'refraction',
      'blur',
      'brightness',
      'saturation',
      'tint',
      'tint-opacity',
      'radius',
      'bezel',
      'specular',
      'interactive',
      'draggable',
      'morph',
      'fixed',
      'adaptive',
      'adaptive-max-brightness',
      'adaptive-max-blur',
      'adaptive-speed'
    ]
  }

  constructor() {
    super()
    if (!HAS_DOM || !this.attachShadow) return
    this._filterId = nextFilterId()
    this._updateFrame = 0
    this._connected = false
    this._onPointerMove = this._onPointerMove.bind(this)
    this._deform = null
    this._onDeformPointerDown = this._onDeformPointerDown.bind(this)
    this._onDeformPointerMove = this._onDeformPointerMove.bind(this)
    this._onDeformTouchMove = this._onDeformTouchMove.bind(this)
    this._onDeformContextMenu = this._onDeformContextMenu.bind(this)
    this._onDeformDragStart = this._onDeformDragStart.bind(this)
    this._onDeformClickCapture = this._onDeformClickCapture.bind(this)
    // 背景自适应状态：当前已应用的偏移量(经 lerp 平滑)、采样调度句柄
    this._adaptiveBrightnessOffset = 0
    this._adaptiveBlurOffset = 0
    this._adaptiveSampleTimer = 0
    this._adaptiveSampling = false
    this._adaptiveScrollHandler = null
    this._lastAdaptiveSampleAt = 0
    this.attachShadow({ mode: 'open' })
    this.shadowRoot.innerHTML = `
      <style>
        :host {
          display: inline-flex;
          position: relative;
          box-sizing: border-box;
          min-width: 0;
          vertical-align: middle;
          border-radius: var(--liquid-glass-radius, 24px);
          /* backdrop-filter 由 JS 直接设置在 host 上：host 位于 light DOM，
             能正常采样背后的真实背景。shadow 内部的 overflow/isolation 会
             在 Chromium/Edge 下阻断 backdrop-filter: url() 的采样，故不放这里。 */
        }
        :host([block]) {
          display: flex;
          width: 100%;
        }
        .surface,
        .specular {
          position: absolute;
          inset: 0;
          border-radius: inherit;
          pointer-events: none;
        }
        .surface {
          z-index: 0;
          border-radius: inherit;
          box-shadow:
            inset 0 0 0 1px var(--liquid-glass-edge, rgba(255,255,255,.32)),
            inset 1.6px 3px 0 -2px rgba(255,255,255,.55),
            inset -2px -2px 0 -2px rgba(255,255,255,.4),
            inset 0 -1px 5px 0 rgba(0,0,0,.08);
        }
        .specular {
          z-index: 1;
          opacity: 0;
          border-radius: inherit;
          backdrop-filter: blur(var(--lg-motion-blur, 0px));
          -webkit-backdrop-filter: blur(var(--lg-motion-blur, 0px));
          background: radial-gradient(
            220px circle at var(--liquid-glass-x, 50%) var(--liquid-glass-y, 50%),
            rgba(255,255,255,var(--liquid-glass-specular, .22)) 0%,
            rgba(255,255,255,0) 65%
          );
          transition: opacity 180ms ease;
        }
        :host(:hover) .specular,
        :host([active]) .specular {
          opacity: 1;
        }
        :host([draggable]) {
          cursor: grab;
          touch-action: pan-y;
          -webkit-touch-callout: none;
          user-select: none;
          -webkit-user-select: none;
        }
        :host([draggable]) .content,
        :host([draggable]) ::slotted(*) {
          user-select: none;
          -webkit-user-select: none;
        }
        :host([draggable][data-lg-lift]) {
          cursor: grabbing;
          z-index: 30;
        }
        .content {
          position: relative;
          z-index: 2;
          display: flex;
          /* 默认垂直堆叠、按正常文档流排列子元素，避免多个块级子元素被
             flex row 挤成一行导致文字竖排碎裂。需要横排的容器(topbar、
             楼层、回复栏)由宿主 class 显式声明 flex-direction:row。 */
          flex-direction: column;
          align-items: stretch;
          justify-content: center;
          flex: 1 1 auto;
          min-width: 0;
          border-radius: inherit;
          overflow: hidden;
        }
        @media (prefers-reduced-motion: reduce) {
          .specular { transition: none; }
        }
      </style>
      <div class="surface" part="surface"></div>
      <div class="specular" part="specular"></div>
      <div class="content" part="content"><slot></slot></div>
    `
    this._surface = this.shadowRoot.querySelector('.surface')
  }

  connectedCallback() {
    if (!HAS_DOM || !this._surface) return
    this._connected = true
    connectedGlasses.add(this)
    if (!performanceDegraded && supportsSvgRefraction()) this._ensureFilter()
    this._resizeObserver ||= new ResizeObserver(() => this._queueUpdate())
    this._resizeObserver.observe(this)
    this.addEventListener('pointermove', this._onPointerMove, { passive: true })
    this._attachDeform()
    this._attachAdaptive()
    this._queueUpdate()
    startPerformanceMonitor()
  }

  disconnectedCallback() {
    this._connected = false
    cancelAnimationFrame(this._updateFrame)
    this._updateFrame = 0
    this._resizeObserver?.disconnect()
    this.removeEventListener('pointermove', this._onPointerMove)
    this._detachDeform()
    this._detachAdaptive()
    this._filter?.remove()
    connectedGlasses.delete(this)
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if ((name === 'draggable' || name === 'morph' || name === 'fixed') && this._connected) {
      // draggable 或 morph 任一开启即启用液态形变;fixed 仅锁位不影响开关
      const enabled = this.options.draggable || this.options.morph
      if (enabled && !this._deform) this._attachDeform()
      if (!enabled && this._deform) this._detachDeform()
    }
    if (name === 'adaptive' && this._connected) {
      const enabled = !(newValue === null || newValue === 'false')
      if (enabled) this._attachAdaptive()
      else this._detachAdaptive()
    }
    if (this._connected) this._queueUpdate()
  }

  get options() {
    return readElementOptions(this)
  }

  configure(options = {}) {
    const attributes = {
      refraction: 'refraction',
      blur: 'blur',
      brightness: 'brightness',
      saturation: 'saturation',
      tint: 'tint',
      tintOpacity: 'tint-opacity',
      radius: 'radius',
      bezel: 'bezel',
      specular: 'specular',
      interactive: 'interactive',
      draggable: 'draggable',
      morph: 'morph',
      fixed: 'fixed',
      adaptive: 'adaptive',
      adaptiveMaxBrightness: 'adaptive-max-brightness',
      adaptiveMaxBlur: 'adaptive-max-blur',
      adaptiveSpeed: 'adaptive-speed'
    }
    Object.entries(attributes).forEach(([key, attribute]) => {
      if (options[key] !== undefined) this.setAttribute(attribute, String(options[key]))
    })
    return this
  }

  refresh() {
    this._update()
    return this
  }

  _onPointerMove(event) {
    if (!this.options.interactive) return
    const rect = this.getBoundingClientRect()
    if (!rect.width || !rect.height) return
    this.style.setProperty('--liquid-glass-x', `${(event.clientX - rect.left) / rect.width * 100}%`)
    this.style.setProperty('--liquid-glass-y', `${(event.clientY - rect.top) / rect.height * 100}%`)
  }

  /* ---------------- 背景自适应（亮度/模糊） ---------------- */

  _attachAdaptive() {
    if (!this.options.adaptive || this._adaptiveScrollHandler) return
    // 背景随滚动/尺寸/内容变化而改变：去抖后重新采样。捕获阶段监听以拿到各滚动容器。
    this._adaptiveScrollHandler = () => this._scheduleAdaptiveSample()
    window.addEventListener('scroll', this._adaptiveScrollHandler, { capture: true, passive: true })
    window.addEventListener('resize', this._adaptiveScrollHandler, { passive: true })
    this._scheduleAdaptiveSample(0)
  }

  _detachAdaptive() {
    if (this._adaptiveScrollHandler) {
      window.removeEventListener('scroll', this._adaptiveScrollHandler, { capture: true })
      window.removeEventListener('resize', this._adaptiveScrollHandler)
      this._adaptiveScrollHandler = null
    }
    clearTimeout(this._adaptiveSampleTimer)
    this._adaptiveSampleTimer = 0
    this._adaptiveSampling = false
    // 完全关闭自适应:无条件把偏移硬归零,并立即用基础值重写 backdropFilter,
    // 杜绝关闭瞬间残留的采样回调/排队 rAF 把已变暗的滤镜留在屏上。
    this._adaptiveBrightnessOffset = 0
    this._adaptiveBlurOffset = 0
    // 无论是否 connected 都取消可能排队中的自适应刷新,避免残留 rAF 把旧偏移画回去
    cancelAnimationFrame(this._updateFrame)
    this._updateFrame = 0
    if (this._connected) {
      this._refreshBackdropCss()
    }
  }

  _scheduleAdaptiveSample(delay = 450) {
    if (!this.options.adaptive || !this._connected) return
    // html2canvas 栅格化整页开销大(几百 ms),交互/滚动会连续触发。加大去抖,
    // 且距上次采样不足冷却期时直接跳过,大幅降低整页栅格化频率,避免卡顿。
    const now = Date.now()
    if (now - this._lastAdaptiveSampleAt < ADAPTIVE_SAMPLE_COOLDOWN_MS) return
    clearTimeout(this._adaptiveSampleTimer)
    this._adaptiveSampleTimer = setTimeout(() => this._sampleBackdrop(), delay)
  }

  async _sampleBackdrop() {
    if (!this.options.adaptive || !this._connected || this._adaptiveSampling) return
    const rect = this.getBoundingClientRect()
    if (!rect.width || !rect.height) return
    const html2canvas = await loadHtml2Canvas()
    if (!html2canvas || !this._connected || !this.options.adaptive) return

    this._adaptiveSampling = true
    // 只 ignore 本元素自身,不再隐藏整页玻璃(那会让每次交互触发采样时整页
    // visibility 切换,看着像整页刷新/闪烁)。本元素被 ignore 后在 canvas 里是
    // 透明像素,analyzeBackdropPixels 会跳过;采样区域外扩一圈,环带是真实背景、
    // 中心透明被跳过,亮度/杂乱度反映玻璃周围真实内容。配合"只提亮不压暗",
    // 浅色页不会误判变黑。
    const margin = Math.max(20, Math.round(Math.max(rect.width, rect.height) * 0.3))
    const sw = rect.width + margin * 2
    const sh = rect.height + margin * 2
    let snapshot = null
    try {
      snapshot = await html2canvas(document.body, {
        x: rect.left + window.scrollX - margin,
        y: rect.top + window.scrollY - margin,
        width: sw,
        height: sh,
        scale: ADAPTIVE_SAMPLE_SIZE / Math.max(sw, sh),
        logging: false,
        backgroundColor: null,
        ignoreElements: (el) => el === this || (el.classList && el.classList.contains('liquid-glass-adaptive-skip'))
      })
    } catch {
      snapshot = null
    }
    this._adaptiveSampling = false
    // 记录完成时间,配合 _scheduleAdaptiveSample 的冷却期,防止交互/滚动
    // 连续触发整页栅格化造成卡顿。
    this._lastAdaptiveSampleAt = Date.now()
    if (!snapshot || !this._connected || !this.options.adaptive) return

    const stats = analyzeBackdropPixels(snapshot)
    if (stats) this._applyAdaptiveStats(stats)
  }

  _applyAdaptiveStats({ luminance, stddev }) {
    // 竞态防护:采样是异步的,若结果回来时 adaptive 已被关闭(或元素断开),
    // 绝不能把偏移重新加上——否则关闭后仍会"无缘无故多一层模糊"。
    if (!this._connected || !this.options.adaptive) return
    const options = this.options
    // 控件是否含文字：含文字时背景杂乱会伤害可读性，需要加模糊压住背景
    const hasText = (this.textContent || '').trim().length > 0
    // 亮度:只在背景明显偏暗时提亮玻璃,绝不在亮色背景上压暗。
    // 浅色页(lum≈0.9,几乎所有浅色页面)已是半透白玻璃、足够清晰,压暗只会
    // 让玻璃无故变灰变黑(用户反复反馈"放久了慢慢变黑")。可读性保护交给
    // 下方的加模糊逻辑;亮度通道只做"暗处提亮"这一件安全的事。
    const brightnessTarget = luminance < 0.45
      ? smoothStep(0.45, 0, luminance) * options.adaptiveMaxBrightness
      : 0
    // 模糊：背景杂乱度(stddev 高,如图片/复杂纹理)驱动；无文字时大幅衰减。
    const clutter = clamp(stddev / 0.25, 0, 1) // 0.25 经验上对应较杂乱背景
    const blurTarget = clutter * options.adaptiveMaxBlur * (hasText ? 1 : 0.3)

    const speed = options.adaptiveSpeed
    this._adaptiveBrightnessOffset += (brightnessTarget - this._adaptiveBrightnessOffset) * speed
    this._adaptiveBlurOffset += (blurTarget - this._adaptiveBlurOffset) * speed
    // 收敛到足够接近目标时吸附，避免无限微调用 update
    if (Math.abs(brightnessTarget - this._adaptiveBrightnessOffset) < 0.001) {
      this._adaptiveBrightnessOffset = brightnessTarget
    }
    if (Math.abs(blurTarget - this._adaptiveBlurOffset) < 0.05) {
      this._adaptiveBlurOffset = blurTarget
    }
    // 轻量刷新:自适应只改 CSS 串接的 blur()/brightness(),位移贴图与滤镜区域
    // 都不变,无需重建 SVG。直接重写 backdropFilter,避免整次 _update 的开销与抖动。
    this._queueAdaptiveRefresh()
  }

  _queueAdaptiveRefresh() {
    if (this._updateFrame) return
    this._updateFrame = requestAnimationFrame(() => {
      this._updateFrame = 0
      this._refreshBackdropCss()
    })
  }

  _refreshBackdropCss() {
    if (!this._connected) return
    const options = this.options
    const value = this._mode === 'refraction' && this._filterId
      ? `url("#${this._filterId}") blur(${this._adaptiveBlur()}px) saturate(${options.saturation}) brightness(${this._adaptiveBrightness()})`
      : `blur(${this._adaptiveBlur()}px) saturate(${options.saturation}) brightness(${this._adaptiveBrightness()})`
    this.style.backdropFilter = value
    this.style.webkitBackdropFilter = value
  }

  _adaptiveBrightness() {
    // adaptive 关闭时直接返回基础亮度,彻底忽略偏移,避免任何残留导致发黑
    if (!this.options.adaptive) return this.options.brightness
    return clamp(this.options.brightness + this._adaptiveBrightnessOffset, 0.5, 2)
  }

  _adaptiveBlur() {
    if (!this.options.adaptive) return this.options.blur
    return clamp(this.options.blur + this._adaptiveBlurOffset, 0, 40)
  }

  /* ---------------- 液态拖拽形变 ---------------- */

  _attachDeform() {
    // draggable(可拖)或 morph(仅形变不拖)任一开启即启用液态形变
    if (!(this.options.draggable || this.options.morph) || this._deform) return
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
    bindDeformGlobalListeners()
    this._deform = {
      pressed: false,
      pointerId: null,
      armed: false,
      isTouch: false,
      pressTimer: 0,
      startX: 0,
      startY: 0,
      maxTravel: 0,
      longPress: false,
      suppressClickUntil: 0,
      x: 0,
      y: 0,
      vx: 0,
      vy: 0,
      lastX: 0,
      lastY: 0,
      scale: 1,
      scaleV: 0,
      pressScale: 1.1,
      maxOffset: 20,
      stretchX: 0,
      stretchY: 0,
      stretchXV: 0,
      stretchYV: 0,
      blur: 0,
      willChangeOn: false
    }
    this.addEventListener('pointerdown', this._onDeformPointerDown)
    this.addEventListener('pointermove', this._onDeformPointerMove)
    this.addEventListener('touchmove', this._onDeformTouchMove, { passive: false })
    this.addEventListener('contextmenu', this._onDeformContextMenu)
    this.addEventListener('dragstart', this._onDeformDragStart)
    this.addEventListener('click', this._onDeformClickCapture, true)
  }

  _detachDeform() {
    if (!this._deform) return
    clearTimeout(this._deform.pressTimer)
    this.removeEventListener('pointerdown', this._onDeformPointerDown)
    this.removeEventListener('pointermove', this._onDeformPointerMove)
    this.removeEventListener('touchmove', this._onDeformTouchMove)
    this.removeEventListener('contextmenu', this._onDeformContextMenu)
    this.removeEventListener('dragstart', this._onDeformDragStart)
    this.removeEventListener('click', this._onDeformClickCapture, true)
    activeDeforms.delete(this)
    this.removeAttribute('data-lg-lift')
    this.style.transform = ''
    this.style.willChange = ''
    this.style.removeProperty('--lg-motion-blur')
    this._deform = null
  }

  _armTouchDrag(fromLongPress = false) {
    const d = this._deform
    if (!d || !d.pressed || d.armed) return
    d.armed = true
    clearTimeout(d.pressTimer)
    d.pressTimer = 0
    // 横向轻移要在第一次 move 就产生位移；纯长按则从当前触点起算，避免跳变。
    d.lastX = fromLongPress ? deformPointer.x : d.startX
    d.lastY = fromLongPress ? deformPointer.y : d.startY
  }

  _onDeformPointerDown(event) {
    const d = this._deform
    if (!d) return
    if (event.button !== undefined && event.button > 0) return // 只响应主键/触摸
    const width = this.offsetWidth
    const height = this.offsetHeight
    if (!width || !height) return
    // 强度随尺寸自适应：小按钮弹性夸张，大面板只轻微鼓动
    d.pressScale = 1 + clamp(28 / Math.hypot(width, height), 0.035, 0.13)
    d.maxOffset = clamp(Math.min(width, height) * 0.2, 5, 25)
    d.pressed = true
    d.pointerId = event.pointerId
    d.isTouch = event.pointerType === 'touch'
    // 鼠标/触控笔立即接管；触屏横向轻移立即接管，静止长按后可向任意方向拖拽。
    d.armed = !d.isTouch
    d.startX = d.lastX = event.clientX
    d.startY = d.lastY = event.clientY
    d.maxTravel = 0
    d.longPress = false
    d.suppressClickUntil = 0
    d.vx = 0
    d.vy = 0
    d.stretchX = 0
    d.stretchY = 0
    d.stretchXV = 0
    d.stretchYV = 0
    d.blur = 0
    deformPointer.x = event.clientX
    deformPointer.y = event.clientY
    try {
      this.setPointerCapture?.(event.pointerId)
    } catch {
      // 某些旧 WebView 不支持指针捕获，继续使用全局监听。
    }
    if (d.isTouch) {
      d.pressTimer = setTimeout(() => {
        d.pressTimer = 0
        if (!d.pressed) return
        this._armTouchDrag(true)
        d.longPress = true
        d.suppressClickUntil = performance.now() + DEFORM_CLICK_SUPPRESS_MS
        try {
          this.setPointerCapture?.(d.pointerId)
        } catch {
          // 兼容不支持 setPointerCapture 的移动端 WebView。
        }
        navigator.vibrate?.(8)
      }, DEFORM_LONG_PRESS_MS)
    }
    if (!d.willChangeOn) {
      this.style.willChange = 'transform, filter'
      d.willChangeOn = true
    }
    activeDeforms.add(this)
    if (!deformFrame) deformFrame = requestAnimationFrame(deformLoop)
  }

  // 关键：拖拽激活后 preventDefault 阻止浏览器把手势接管成滚动（非被动监听）。
  // 激活前纵向滑走则判定为滚动，主动放弃形变。
  _onDeformTouchMove(event) {
    const d = this._deform
    if (!d || !d.pressed) return
    const touch = event.touches[0]
    if (!touch) return
    deformPointer.x = touch.clientX
    deformPointer.y = touch.clientY
    d.maxTravel = Math.max(d.maxTravel, Math.hypot(touch.clientX - d.startX, touch.clientY - d.startY))
    if (d.armed) {
      event.preventDefault()
    } else if (Math.hypot(touch.clientX - d.startX, touch.clientY - d.startY) > DEFORM_SLOP_PX) {
      clearTimeout(d.pressTimer)
      d.pressTimer = 0
      this._releaseDeform()
    }
  }

  _onDeformPointerMove(event) {
    const d = this._deform
    if (!d || !d.pressed || !d.isTouch || event.pointerId !== d.pointerId) return
    const dx = event.clientX - d.startX
    const dy = event.clientY - d.startY
    const distance = Math.hypot(dx, dy)
    deformPointer.x = event.clientX
    deformPointer.y = event.clientY
    d.maxTravel = Math.max(d.maxTravel, distance)

    if (d.armed) {
      event.preventDefault()
      return
    }
    if (distance <= DEFORM_SLOP_PX) return

    // 横向滑动优先视为液态拖拽；纵向滑动保留给页面滚动，直到长按完成后再接管。
    if (Math.abs(dx) >= Math.abs(dy)) {
      this._armTouchDrag()
      event.preventDefault()
    } else {
      clearTimeout(d.pressTimer)
      d.pressTimer = 0
      this._releaseDeform(event)
    }
  }

  // Android 长按弹系统菜单、iOS 长按出选中气泡，都会打断拖拽
  _onDeformContextMenu(event) {
    const d = this._deform
    if (d && (d.pressed || d.suppressClickUntil > performance.now())) event.preventDefault()
  }

  // 阻止拖出原生 ghost image，避免抢走液态玻璃的指针交互
  _onDeformDragStart(event) {
    if (this._deform) event.preventDefault()
  }

  // 拖拽后松手会触发 click：位移明显时这一次点击视为拖拽而非点按
  _onDeformClickCapture(event) {
    const d = this._deform
    if (!d || d.suppressClickUntil <= performance.now()) return
    d.suppressClickUntil = 0
    event.preventDefault()
    event.stopPropagation()
  }

  _releaseDeform(event) {
    const d = this._deform
    if (!d || !d.pressed) return
    const releaseX = event?.clientX ?? deformPointer.x
    const releaseY = event?.clientY ?? deformPointer.y
    d.maxTravel = Math.max(d.maxTravel, Math.hypot(releaseX - d.startX, releaseY - d.startY))
    const shouldSuppressClick = d.longPress || d.maxTravel > DEFORM_CLICK_SUPPRESS_PX
    d.pressed = false
    d.pointerId = null
    d.armed = false
    clearTimeout(d.pressTimer)
    d.pressTimer = 0
    this.removeAttribute('data-lg-lift')
    d.suppressClickUntil = shouldSuppressClick
      ? performance.now() + DEFORM_CLICK_SUPPRESS_MS
      : 0
  }

  _stepDeform() {
    const d = this._deform
    if (!d) {
      activeDeforms.delete(this)
      return
    }

    if (d.pressed) {
      d.maxTravel = Math.max(d.maxTravel, Math.hypot(deformPointer.x - d.startX, deformPointer.y - d.startY))
      // fixed 锁位:保留按压/拉伸/高光等交互反馈,但不产生位移、不可拖拽移动
      const lockMove = this.options.fixed
      if (d.armed && !lockMove) {
        // 流体跟随：指针增量 → 目标速度 → 平滑积分 → 位移钳制
        const dx = deformPointer.x - d.lastX
        const dy = deformPointer.y - d.lastY
        const moveRatio = d.isTouch ? DEFORM_TOUCH_MOVE_RATIO : DEFORM_MOVE_RATIO
        d.vx += (dx * moveRatio - d.vx) * (1 - DEFORM_FLUID_SMOOTH)
        d.vy += (dy * moveRatio - d.vy) * (1 - DEFORM_FLUID_SMOOTH)
        d.x = clamp(d.x + d.vx, -d.maxOffset, d.maxOffset)
        d.y = clamp(d.y + d.vy, -d.maxOffset, d.maxOffset)
      }
      // 触屏判定期间只给轻微按压反馈，拖拽接管后再进入完整液态放大态。
      const targetScale = d.isTouch && !d.armed ? 1.02 : d.pressScale
      d.scale += (targetScale - d.scale) * 0.3
      d.scaleV = 0

      // 直接对长宽做二维形变：位移决定持续拉伸量，速度补充甩动时的瞬时张力。
      const speed = Math.hypot(d.vx, d.vy)
      const pullX = Math.max(
        clamp(Math.abs(d.x) / d.maxOffset, 0, 1),
        clamp(Math.abs(d.vx) / 4, 0, 1) * 0.72
      )
      const pullY = Math.max(
        clamp(Math.abs(d.y) / d.maxOffset, 0, 1),
        clamp(Math.abs(d.vy) / 4, 0, 1) * 0.72
      )
      const targetStretchX = (pullX - pullY * DEFORM_CROSS_SQUASH_RATIO) * DEFORM_AXIS_STRETCH_MAX
      const targetStretchY = (pullY - pullX * DEFORM_CROSS_SQUASH_RATIO) * DEFORM_AXIS_STRETCH_MAX
      d.stretchX += (targetStretchX - d.stretchX) * DEFORM_STRETCH_LERP
      d.stretchY += (targetStretchY - d.stretchY) * DEFORM_STRETCH_LERP
      d.stretchXV = 0
      d.stretchYV = 0

      // 动态模糊：高速位移时轻微糊化，松手/减速时回落，强化“液体流动”观感
      const targetBlur = clamp(speed / 40, 0, 1) * DEFORM_MOTION_BLUR_MAX
      d.blur += (targetBlur - d.blur) * 0.18
    } else {
      // 松手：位置线性回弹，缩放用欠阻尼弹簧带一点过冲（QQ 弹弹）
      d.x += (0 - d.x) * DEFORM_RESET_LERP
      d.y += (0 - d.y) * DEFORM_RESET_LERP
      d.vx *= 0.85
      d.vy *= 0.85
      d.scaleV = (d.scaleV + (1 - d.scale) * 0.16) * 0.7
      d.scale += d.scaleV

      // 宽高分别走欠阻尼弹簧回到原始比例，保留一段果冻式过冲。
      d.stretchXV = (d.stretchXV + (0 - d.stretchX) * 0.2) * 0.66
      d.stretchYV = (d.stretchYV + (0 - d.stretchY) * 0.2) * 0.66
      d.stretchX += d.stretchXV
      d.stretchY += d.stretchYV
      d.blur *= 0.82 // 模糊快速消散

      const settled =
        Math.abs(d.x) < 0.1 && Math.abs(d.y) < 0.1 &&
        Math.abs(d.scale - 1) < 0.001 && Math.abs(d.scaleV) < 0.001 &&
        Math.abs(d.stretchX) < 0.001 && Math.abs(d.stretchY) < 0.001 &&
        Math.abs(d.stretchXV) < 0.001 && Math.abs(d.stretchYV) < 0.001 &&
        d.blur < 0.02
      if (settled) {
        this.style.transform = ''
        this.style.removeProperty('--lg-motion-blur')
        this.removeAttribute('data-lg-lift')
        if (d.willChangeOn) {
          this.style.willChange = ''
          d.willChangeOn = false
        }
        activeDeforms.delete(this)
        return
      }
    }

    d.lastX = deformPointer.x
    d.lastY = deformPointer.y
    // 形变叠加到 transform 而非 translate/scale 属性:作者常用 translate:-50% -50%
    // 做居中(如超大玻璃),若直接覆写 translate 会把居中位移冲掉导致元素瞬间跳位。
    // transform 与 translate/scale 是独立叠加的,互不干扰。
    const widthScale = d.scale * (1 + d.stretchX)
    const heightScale = d.scale * (1 + d.stretchY)
    this.style.transform = `translate(${d.x.toFixed(2)}px, ${d.y.toFixed(2)}px) scale(${widthScale.toFixed(4)}, ${heightScale.toFixed(4)})`

    // 动态模糊经 CSS 变量交给 .specular 的 backdrop-filter，与基础模糊连贯叠加
    if (d.blur > 0.02) {
      this.style.setProperty('--lg-motion-blur', `${d.blur.toFixed(2)}px`)
    } else if (this.style.getPropertyValue('--lg-motion-blur')) {
      this.style.removeProperty('--lg-motion-blur')
    }

    // 放大/拖拽期间浮起
    if (d.pressed && d.armed) {
      this.setAttribute('data-lg-lift', '')
    } else {
      this.removeAttribute('data-lg-lift')
    }
  }

  _queueUpdate() {
    if (this._updateFrame) return
    this._updateFrame = requestAnimationFrame(() => {
      this._updateFrame = 0
      this._update()
    })
  }

  _ensureFilter() {
    if (!this._filter) {
      this._filter = document.createElementNS(SVG_NS, 'filter')
      this._filter.id = this._filterId
      this._filter.setAttribute('filterUnits', 'userSpaceOnUse')
      this._filter.setAttribute('primitiveUnits', 'userSpaceOnUse')
      this._filter.setAttribute('color-interpolation-filters', 'sRGB')
      // x/y/width/height 由 _update 按 padding 安全区设置，这里不预设。

      // Blog 项目验证过的可靠形式：位移直接作用于 SourceGraphic(背景)，
      // 模糊/饱和/亮度用 CSS 串接在 url() 之后，而非烘焙进 SVG 滤镜。
      this._mapNode = document.createElementNS(SVG_NS, 'feImage')
      this._mapNode.setAttribute('result', 'map')
      this._mapNode.setAttribute('preserveAspectRatio', 'none')
      this._displacementNode = document.createElementNS(SVG_NS, 'feDisplacementMap')
      this._displacementNode.setAttribute('in', 'SourceGraphic')
      this._displacementNode.setAttribute('in2', 'map')
      this._displacementNode.setAttribute('xChannelSelector', 'R')
      this._displacementNode.setAttribute('yChannelSelector', 'G')
      this._filter.append(this._mapNode, this._displacementNode)
    }
    if (!this._filter.isConnected) ensureFilterRoot().appendChild(this._filter)
  }

  _applyGaussianFallback(reason = 'unsupported', fps = null) {
    const options = this.options
    const fallback = `blur(${this._adaptiveBlur()}px) saturate(${options.saturation}) brightness(${this._adaptiveBrightness()})`
    const modeChanged = this._mode !== 'gaussian'
    this._mode = 'gaussian'
    this.setAttribute('data-liquid-glass-mode', 'gaussian')
    // 写到 host 自身：host 在 light DOM 中，能正常采样背后真实背景。
    this.style.backdropFilter = fallback
    this.style.webkitBackdropFilter = fallback
    this._filter?.remove()

    if (modeChanged && reason === 'low-fps') {
      this.dispatchEvent(new CustomEvent('liquid-glass-degraded', {
        bubbles: false,
        composed: false,
        detail: {
          reason,
          fps,
          threshold: performanceOptions.fpsThreshold,
          fallback: 'gaussian'
        }
      }))
    }
  }

  _update() {
    if (!this._connected || !this._surface) return
    const width = Math.round(this.offsetWidth)
    const height = Math.round(this.offsetHeight)
    if (!width || !height) return

    const options = this.options
    const radius = Math.min(options.radius, width / 2, height / 2)
    const halfMinSide = Math.min(width, height) / 2
    const bezel = Math.min(halfMinSide, Math.max(6, Math.min(24, halfMinSide * options.bezel)))
    // Blog 项目验证过的做法：位移贴图外扩一圈 padding 安全区，滤镜区域相应外扩
    // (filter x/y = -padding)。否则折射把背景向元素中心拉时，上下/左右边缘会采样到
    // 滤镜边界外的空白，形成浑浊的"露馅"拉伸带 —— 横向胶囊的上下边缘尤其明显。
    // 注意:padding 用基础 blur(options.blur)而非自适应 blur。自适应偏移会让 padding
    // 随背景抖动,导致位移贴图缓存每次 miss、整张贴图反复重建、滤镜区域对不齐,
    // 折射(扭曲)因此糊掉甚至消失(用户反馈"自适应一开扭曲就没")。位移贴图只需按
    // 基础模糊外扩;自适应只调 CSS 串接的 blur()/brightness(),不影响贴图,保持稳定。
    const padding = Math.ceil(clamp(
      options.blur * 3 + bezel * options.refraction,
      MIN_FILTER_PADDING_PX,
      MAX_FILTER_PADDING_PX
    ))

    this.style.setProperty('--liquid-glass-radius', `${options.radius}px`)
    this.style.setProperty('--liquid-glass-specular', String(options.interactive ? options.specular : 0))
    // 用 rgba() 而非 color-mix():html2canvas 无法解析 color-mix,
    // 自适应采样栅格化整页时会抛错;同时兼容不支持 color-mix 的旧浏览器。
    this._surface.style.background = tintToRgba(options.tint, options.tintOpacity)

    if (performanceDegraded || !supportsSvgRefraction()) {
      this._applyGaussianFallback(performanceDegraded ? 'low-fps' : 'unsupported', lastMeasuredFps)
      return
    }

    this._ensureFilter()
    const map = buildDisplacementMap(
      width,
      height,
      radius,
      bezel,
      options.refraction,
      padding
    )
    // 滤镜区域与 feImage 都外扩到 [-padding, -padding, mapWidth, mapHeight]，
    // 与位移贴图（以元素中心为原点、含 padding 边距）严格对齐。
    const fx = -padding
    const fy = -padding
    this._filter.setAttribute('x', String(fx))
    this._filter.setAttribute('y', String(fy))
    this._filter.setAttribute('width', String(map.mapWidth))
    this._filter.setAttribute('height', String(map.mapHeight))
    this._mapNode.setAttribute('x', String(fx))
    this._mapNode.setAttribute('y', String(fy))
    this._mapNode.setAttribute('width', String(map.mapWidth))
    this._mapNode.setAttribute('height', String(map.mapHeight))
    this._mapNode.setAttribute('href', map.url)
    this._displacementNode.setAttribute('scale', map.scale.toFixed(2))
    // 位移交给 SVG,模糊/饱和/亮度用 CSS 串接（与 Blog 一致，Edge/Chrome 可靠）。
    // 模糊与亮度叠加上自适应偏移（adaptive 开启时根据背景内容动态计算）。
    const filterValue = `url("#${this._filterId}") blur(${this._adaptiveBlur()}px) saturate(${options.saturation}) brightness(${this._adaptiveBrightness()})`
    this._mode = 'refraction'
    this.setAttribute('data-liquid-glass-mode', 'refraction')
    this.style.backdropFilter = filterValue
    this.style.webkitBackdropFilter = filterValue
  }
}

const propertyAttributes = {
  refraction: 'refraction',
  blur: 'blur',
  brightness: 'brightness',
  saturation: 'saturation',
  tint: 'tint',
  tintOpacity: 'tint-opacity',
  radius: 'radius',
  bezel: 'bezel',
  specular: 'specular',
  interactive: 'interactive',
  draggable: 'draggable',
  adaptive: 'adaptive',
  adaptiveMaxBrightness: 'adaptive-max-brightness',
  adaptiveMaxBlur: 'adaptive-max-blur',
  adaptiveSpeed: 'adaptive-speed'
}

Object.entries(propertyAttributes).forEach(([property, attribute]) => {
  Object.defineProperty(LiquidGlassElement.prototype, property, {
    get() {
      return this.options[property]
    },
    set(value) {
      this.setAttribute(attribute, String(value))
    }
  })
})

export function defineLiquidGlass(tagName = 'liquid-glass') {
  if (!HAS_DOM || !window.customElements) return null
  if (!window.customElements.get(tagName)) {
    window.customElements.define(tagName, LiquidGlassElement)
  }
  return window.customElements.get(tagName)
}
